import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { CircularProgress } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import Report from '../../../components/Report';
import Cross from '../../../images/cross.svg?react';
import DownloadIcon from '../../../images/download-center.svg?react';
import Help from '../../../images/help.svg?react';
import LanguageControl from '../../../shared/components/LanguageControl';
import DigitInput from './DigitInput';

const LightTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
    ({ theme }: any) => ({
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.common.white,
            color: 'rgba(0, 0, 0, 0.87)',
            boxShadow: theme.shadows[3],
            fontSize: 10,
            padding: '10px 5px',
        },
    })
);

function DownloadView({ state, actions }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Download' });
    const { inputValue, page, lockInput, reportReady, certReady, reportData, isPreparing, message, hasError } = state;

    const { setInputValue, confirmDevice, reset, downloadReport, downloadCert } = actions;

    const reportRef = useRef(null);

    const reportHandlers = useMemo(
        () => ({
            onDownload: () => downloadReport(reportRef.current),
        }),
        [downloadReport]
    );

    const certHandlers = useMemo(
        () => ({
            onDownload: () => downloadCert(),
        }),
        [downloadCert]
    );

    return (
        <div className='relative min-h-screen bg-[#F5F5F5] md:px-4 py-0 sm:px-6 md:py-12 flex items-center'>
            <div className='mx-auto flex w-screen flex-col justify-center bg-white px-6 pb-10 pt-6 shadow-[0_20px_45px_rgba(37,46,66,0.08)] min-h-screen md:w-full md:max-w-[420px] md:min-h-[30rem] md:h-auto md:rounded-[32px]'>
                {page === 2 && (
                    <div className='flex justify-start mb-4'>
                        <button
                            onClick={() => reset()}
                            className='p-2 cursor-pointer transition-colors hover:bg-gray-100 rounded-full'
                            title='Go back'
                        >
                            <KeyboardArrowLeftIcon className='text-gray-600' />
                        </button>
                    </div>
                )}
                <div className='mt-6 flex  items-center text-center'>
                    <button
                        onClick={() => (window.location.href = '/')}
                        className='cursor-pointer transition-transform hover:scale-105'
                        title='Go to Home'
                    >
                        <DownloadIcon className='h-[80px] w-[80px]' />
                    </button>
                    <div className='max-w-[300px]'>
                        <h1 className='mt-6 text-[1.75rem] font-extrabold text-[#1F252E]'>{t('title')}</h1>
                        <p className='mt-2 w-full text-sm font-medium leading-6 text-[#757F8E]'>{t('subtitle')}</p>
                    </div>
                </div>

                <IDForm
                    inputValue={inputValue}
                    setInputValue={setInputValue}
                    lockInput={lockInput}
                    confirmDevice={confirmDevice}
                    hasError={hasError}
                />

                <DownloadActions
                    page={page}
                    confirmDevice={confirmDevice}
                    reportReady={reportReady}
                    certReady={certReady}
                    onDownloadReport={reportHandlers.onDownload}
                    onDownloadCert={certHandlers.onDownload}
                />

                <div className='mt-8 flex flex-col items-center gap-6 text-center text-xs text-[#8A94A6]'>
                    <p>
                        {t('footer')}{' '}
                        <a className='font-semibold text-[#2763EA]' href='mailto: support@gocochain.com'>
                            support@gocochain.com
                        </a>
                    </p>
                    <LanguageControl className='hidden md:flex' />
                    <p className='text-[11px] text-[#A0A8B6]'>© 2026 Gocochain Co., Ltd. All rights reserved.</p>
                    <p>version {import.meta.env.VITE_APP_VERSION ?? 'dev'}</p>
                </div>
            </div>

            <div className='absolute bottom-6 left-0 right-0 flex justify-center md:hidden'>
                <LanguageControl />
            </div>

            <Snackbar
                open={isPreparing}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{
                    backgroundColor: '#333333',
                    color: '#FFFFFF',
                    padding: '0 1rem',
                    margin: '0.8rem auto',
                    position: 'absolute',
                    maxWidth: '20rem',
                    breakPoint: 'xxs',
                }}
            >
                <p>
                    <CircularProgress size='1rem' style={{ verticalAlign: 'text-top' }} /> {message}
                </p>
            </Snackbar>

            {reportReady ? (
                <div id='report' ref={reportRef} className='absolute left-[-1000px] top-0 block w-[595pt]'>
                    <Report reportData={reportData} />
                </div>
            ) : null}
        </div>
    );
}

function IDForm({ inputValue, setInputValue, lockInput, confirmDevice, hasError }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Download' });
    return (
        <div className='mt-8 flex flex-col items-center text-center'>
            <div className='flex items-center gap-1 text-base font-semibold text-[#1F252E]'>
                <span>{t('deviceId')}</span>
                <LightTooltip title={t('hint')} placement='top'>
                    <span className='inline-flex cursor-help'>
                        <Help className='h-4 w-4 text-[#9CA3B0]' />
                    </span>
                </LightTooltip>
            </div>
            <div className='mt-5 w-full'>
                <DigitInput
                    value={inputValue}
                    setInputValue={setInputValue}
                    lock={lockInput}
                    handleConfirm={confirmDevice}
                />
            </div>
            <div
                className={`mt-3 min-h-[24px] text-sm text-[#FF4E4E] transition ${hasError ? 'opacity-100' : 'opacity-0'}`}
            >
                <p className='flex items-center justify-center gap-2'>
                    <Cross className='h-3 w-3' /> Please check your Device ID
                </p>
            </div>
        </div>
    );
}

function DownloadActions({ page, confirmDevice, reportReady, certReady, onDownloadReport, onDownloadCert }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Download' });
    if (page === 1) {
        return (
            <div className='mt-4 flex flex-col gap-3'>
                <button
                    type='button'
                    className='h-12 w-full rounded-full bg-gradient-to-r from-[#4A91FF] to-[#2763EA] text-base font-semibold text-white shadow-[0_10px_20px_rgba(39,99,234,0.35)] transition hover:brightness-105 cursor-pointer disabled:cursor-not-allowed disabled:bg-[#E5EAF1] disabled:text-[#B8C0CC] disabled:shadow-none'
                    onClick={confirmDevice}
                >
                    {t('confirm')}
                </button>
            </div>
        );
    }
    if (page === 2) {
        return (
            <div className='mt-4 flex flex-col gap-3'>
                <button
                    type='button'
                    className='h-12 w-full rounded-full bg-gradient-to-r from-[#4A91FF] to-[#2763EA] text-base font-semibold text-white shadow-[0_10px_20px_rgba(39,99,234,0.35)] transition hover:brightness-105 cursor-pointer disabled:cursor-not-allowed disabled:!bg-[#EAE9E9] disabled:text-[#BEC0CA] disabled:shadow-none disabled:!bg-none'
                    disabled={!reportReady}
                    onClick={onDownloadReport}
                >
                    {t('report')}
                </button>
                <button
                    type='button'
                    className='h-12 w-full rounded-full bg-gradient-to-r from-[#4A91FF] to-[#2763EA] text-base font-semibold text-white shadow-[0_10px_20px_rgba(39,99,234,0.35)] transition hover:brightness-105 cursor-pointer disabled:cursor-not-allowed disabled:!bg-[#EAE9E9] disabled:text-[#BEC0CA] disabled:shadow-none disabled:!bg-none'
                    disabled={!certReady}
                    onClick={onDownloadCert}
                >
                    {t('certificate')}
                </button>
            </div>
        );
    }

    return null;
}

export default DownloadView;
